/**
 * EmailDeliveryAudit.jsx — pipeline-accordion email panel.
 *
 * Replaces ReadReceipts in the matter accordion. Scope (Vanessa 19 May 2026):
 *   1. Every email tied to the matter — internal + external.
 *   2. Per-row icons: Sent, Delivered, Bounced, Opened.
 *   3. Header summary pills tallying the four states.
 *
 * "Delivered" means delivered TO THE CLIENT (no NDR within the bounce-quiet
 * window), not delivered to Microsoft. The backend endpoint
 * GET /email-audit/delivery/{matter_reference} resolves the state by joining
 * email_audit + email_bounces and applying the window rule. Defaults to
 * 30 minutes; override via ?bounce_window_minutes=N.
 *
 * No silent fallbacks — errors are surfaced via window.errorMessage when
 * available, else a visible inline message.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import {
  Mail,
  MailCheck,
  MailWarning,
  MailX,
  Eye,
  Send,
  Inbox,
  RefreshCw,
} from "lucide-react";

function fmt(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return (
      d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) +
      " " +
      d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return String(ts).slice(0, 16);
  }
}

function StatePill({ label, count, color, Icon, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${color}`}
    >
      <Icon className="w-3 h-3" />
      {label} {count}
    </span>
  );
}

function StateIcon({ state, opened }) {
  // Stacked: delivery state on the left, opened on the right.
  if (state === "bounced") {
    return (
      <MailX
        className="w-3.5 h-3.5 text-status-red flex-shrink-0"
        aria-label="Bounced"
      />
    );
  }
  if (state === "delivered") {
    return (
      <MailCheck
        className="w-3.5 h-3.5 text-status-green flex-shrink-0"
        aria-label="Delivered to client"
      />
    );
  }
  if (state === "failed" || state === "disabled") {
    return (
      <MailWarning
        className="w-3.5 h-3.5 text-status-amber flex-shrink-0"
        aria-label={state === "failed" ? "Failed" : "Email service disabled"}
      />
    );
  }
  if (state === "received") {
    return (
      <Inbox
        className="w-3.5 h-3.5 text-teal flex-shrink-0"
        aria-label="Received"
      />
    );
  }
  // sent | queued | unknown
  return (
    <Send
      className="w-3.5 h-3.5 text-txt-muted flex-shrink-0"
      aria-label="Sent (awaiting confirmation)"
    />
  );
}

function stateLabel(state) {
  switch (state) {
    case "bounced":
      return "Bounced";
    case "delivered":
      return "Delivered to client";
    case "failed":
      return "Failed";
    case "disabled":
      return "Email service was off";
    case "received":
      return "Reply received";
    case "queued":
      return "Queued";
    case "unknown":
      return "Unknown";
    default:
      return "Sent (awaiting bounce window)";
  }
}

function recipientLabel(row) {
  if ((row.direction || "sent") === "received") {
    return row.from_address || "(unknown sender)";
  }
  const tos = row.to_addresses || [];
  if (tos.length === 0) return row.client_email || "(no recipient)";
  if (tos.length === 1) return tos[0];
  return `${tos[0]} +${tos.length - 1}`;
}

export default function EmailDeliveryAudit({
  matterReference,
  compact = false,
  bounceWindowMinutes = 30,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!matterReference) return;
    setLoading(true);
    setError(null);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      const resp = await axios.get(
        `${API}/email-audit/delivery/${encodeURIComponent(matterReference)}`,
        {
          params: { bounce_window_minutes: bounceWindowMinutes },
          headers: { "X-Bonsai-User-Id": userId },
        },
      );
      setData(resp.data || null);
    } catch (err) {
      const fallback = "Could not load email audit for this matter.";
      const msg = errorMessage(err, fallback);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [matterReference, bounceWindowMinutes]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!matterReference) return null;

  const summary = data?.summary || {
    sent: 0,
    delivered: 0,
    bounced: 0,
    opened: 0,
    failed: 0,
    received: 0,
  };
  const rows = data?.rows || [];

  return (
    <div
      className={
        compact
          ? "bg-navy border border-navy-border rounded p-2 text-xs"
          : "bg-navy border border-navy-border rounded-lg p-4"
      }
      data-testid="email-delivery-audit"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3
          className={
            compact
              ? "text-[11px] font-bold text-white flex items-center gap-1"
              : "text-sm font-bold text-white flex items-center gap-2"
          }
        >
          <Mail className={compact ? "w-3 h-3 text-teal" : "w-4 h-4 text-teal"} />
          Email Audit
        </h3>
        <div className="flex items-center gap-1 flex-wrap">
          <StatePill
            label="Sent"
            count={summary.sent}
            Icon={Send}
            color="text-txt-muted border-navy-border"
            testId="pill-sent"
          />
          <StatePill
            label="Delivered"
            count={summary.delivered}
            Icon={MailCheck}
            color="text-status-green border-status-green/40"
            testId="pill-delivered"
          />
          <StatePill
            label="Bounced"
            count={summary.bounced}
            Icon={MailX}
            color="text-status-red border-status-red/40"
            testId="pill-bounced"
          />
          <StatePill
            label="Opened"
            count={summary.opened}
            Icon={Eye}
            color="text-teal border-teal/40"
            testId="pill-opened"
          />
          {summary.failed > 0 && (
            <StatePill
              label="Failed"
              count={summary.failed}
              Icon={MailWarning}
              color="text-status-amber border-status-amber/40"
              testId="pill-failed"
            />
          )}
          {summary.received > 0 && (
            <StatePill
              label="Received"
              count={summary.received}
              Icon={Inbox}
              color="text-teal border-teal/40"
              testId="pill-received"
            />
          )}
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-audit"
            className="ml-1 p-1 rounded hover:bg-navy-border/60 text-txt-muted disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw
              className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {error && (
        <div
          data-testid="audit-error"
          className="text-[11px] text-status-red bg-status-red/10 border border-status-red/30 rounded px-2 py-1 mb-2"
        >
          {error}
        </div>
      )}

      {!error && rows.length === 0 && !loading && (
        <p className="text-[11px] text-txt-muted italic">
          No emails recorded for this matter yet.
        </p>
      )}

      <div className="space-y-1.5">
        {rows.map((r, idx) => {
          const state = r.delivered_state || "unknown";
          const opened = !!r.opened_at;
          return (
            <div
              key={`${r.message_id || r.graph_message_id || idx}-${idx}`}
              data-testid={`audit-row-${idx}`}
              data-state={state}
              className="flex items-start justify-between bg-navy rounded px-3 py-2 border border-navy-border/50"
            >
              <div className="flex items-start gap-2 min-w-0">
                <div className="flex items-center gap-0.5 pt-0.5">
                  <StateIcon state={state} opened={opened} />
                  {opened && (
                    <Eye
                      className="w-3.5 h-3.5 text-teal flex-shrink-0"
                      aria-label="Opened by client"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-white truncate">
                    {recipientLabel(r)}
                  </p>
                  <p className="text-[10px] text-txt-muted truncate">
                    {(r.template_key || "email").replace(/_/g, " ")} ·{" "}
                    {r.subject || "(no subject)"}
                  </p>
                  {state === "bounced" && r.bounce_reason && (
                    <p className="text-[10px] text-status-red truncate">
                      {r.bounce_reason}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-3">
                <span
                  className={
                    state === "delivered"
                      ? "text-[10px] text-status-green"
                      : state === "bounced"
                        ? "text-[10px] text-status-red"
                        : state === "failed" || state === "disabled"
                          ? "text-[10px] text-status-amber"
                          : "text-[10px] text-txt-muted"
                  }
                >
                  {stateLabel(state)}
                </span>
                <p className="text-[9px] text-txt-muted">
                  {fmt(r.sent_at || r.delivered_at || r.created_at)}
                </p>
                {opened && (
                  <p className="text-[9px] text-teal">
                    Opened {fmt(r.opened_at)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
